import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowUpRight, PenLine } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { CONTACT } from "@/data/site";

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_REVIEWS = [
  {
    author: "Daniele Cerioni",
    meta: "Local Guide · Google",
    title: "Grandissime professioniste",
    text: "Società molto valida di corsi di formazione per la sicurezza sul lavoro! Un ringraziamento particolare a Simona ed Emanuela, grandissime professioniste!",
  },
  {
    author: "Ciro Cavaccini",
    meta: "Recensione Google",
    title: "Docente preparata e disponibile",
    text: "Docente preparata e disponibile all'ascolto delle domande e risolvere ogni dubbio. Consigliato!",
  },
  {
    author: "Giancarlo Smerilli",
    meta: "Recensione Google",
    title: "Ottimo corso",
    text: "Ottimo corso e bravissima formatrice.",
  },
];

const Stars = ({ n = 5 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n} stelle su 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} className={i < n ? "fill-brand-red text-brand-red" : "text-border"} />
    ))}
  </span>
);

export const TrustpilotReviews = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/reviews`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d.configured && (d.reviews || []).length > 0) setLive(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const reviews = live
    ? live.reviews.map((r) => ({
        author: r.author || "Utente Google",
        meta: r.relative_time ? `${r.relative_time} · Google` : "Recensione Google",
        photo: r.author_photo_uri,
        text: r.text,
        rating: r.rating || 5,
      }))
    : FALLBACK_REVIEWS.map((r) => ({ ...r, rating: 5 }));

  const ratingText = live?.rating ? String(live.rating).replace(".", ",") : "4,9";
  const countText = live?.user_rating_count
    ? `${live.user_rating_count} recensioni su Google`
    : "15 recensioni su Google";
  const reviewsLink = live?.google_maps_uri || CONTACT.googleReviews;

  return (
    <section id="recensioni" data-testid="reviews-section" className="py-24 md:py-32 bg-brand-paper border-t border-border">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 mb-14">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">
              <FcGoogle size={18} /> Recensioni Google
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tighter text-brand-black leading-none">
              Dicono di noi
            </h2>
          </div>
          <div className="flex items-center gap-4 lg:pt-8">
            <div className="font-heading font-black text-5xl text-brand-black" data-testid="google-rating">{ratingText}</div>
            <div>
              <Stars n={live?.rating ? Math.round(live.rating) : 5} />
              <div className="text-xs text-brand-ink mt-1" data-testid="google-count">{countText}</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.article
              key={r.author}
              data-testid={`google-review-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              className="border border-border bg-white p-6 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                {r.photo ? (
                  <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-brand-black text-white flex items-center justify-center font-bold">
                    {r.author.charAt(0)}
                  </div>
                )}
                <div>
                  <div className="font-semibold text-brand-black">{r.author}</div>
                  <div className="text-xs text-brand-ink">{r.meta}</div>
                </div>
              </div>
              <Stars n={r.rating} />
              {r.title && (
                <h3 className="mt-3 font-heading font-bold text-lg text-brand-black leading-tight">{r.title}</h3>
              )}
              <p className="mt-2 text-sm text-brand-black/80 leading-relaxed">{r.text}</p>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={reviewsLink}
            target="_blank"
            rel="noreferrer"
            data-testid="google-read-link"
            className="inline-flex items-center gap-2 border border-brand-black text-brand-black font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-brand-black hover:text-white transition-colors"
          >
            Tutte le recensioni <ArrowUpRight size={16} />
          </a>
          <a
            href={reviewsLink}
            target="_blank"
            rel="noreferrer"
            data-testid="google-write-link"
            className="inline-flex items-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-brand-black transition-colors"
          >
            Lascia una recensione <PenLine size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};
